import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Text, { TextLink } from '@lightspeed/cirrus-text';
import { Button } from '@lightspeed/flame/Button';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import { Tooltip } from '@lightspeed/flame/Tooltip';
import { IconDelete } from '@lightspeed/flame/Icon/Delete';
import Table from 'cirrus/Table';
import { hasRetailManagerContext } from 'pages/utils/retail-manager';
import first from 'lodash/first';
import get from 'lodash/get';

const getTerminalHeaders = terminals => {
  // we only need one terminal to figure out what to display
  const terminal = first(terminals);
  const cells = [
    {
      key: 'name',
      content: 'Terminal Name',
      className: 'cr-truncate',
      width: '40%',
    },
  ];

  // Add gateway specific headers
  if (terminal) {
    const terminalAddress = get(terminal, 'configuration.address');
    if (terminalAddress) {
      cells.push({
        key: 'address',
        content: 'IP Address',
        className: 'cr-truncate',
        width: '20%',
      });
    }
    const terminalSerialNumber = get(terminal, 'configuration.serialNumber');
    if (terminalSerialNumber) {
      cells.push({
        key: 'serialNumber',
        content: 'Serial Number',
        className: 'cr-truncate',
        width: '20%',
      });
    }
  }

  cells.push({
    key: 'lastActiveIn',
    content: 'Last Active In',
    className: 'cr-truncate',
    align: 'right',
    width: '15%',
  });

  cells.push({
    key: 'delete',
    content: '',
    className: 'cr-truncate',
    width: '5%',
  });

  return {
    cells,
  };
};

const getTerminalRows = (terminals, onEdit, onDelete) => {
  let rows = [];
  if (terminals.length) {
    rows = terminals.map(terminal => {
      const cells = [
        {
          content: (
            <TextLink
              onClick={() => {
                onEdit(terminal);
              }}
              title={get(terminal, 'name', 'n/a')}
            >
              <var>{get(terminal, 'name', '')}</var>
            </TextLink>
          ),
          className: 'cr-bold cr-truncate',
        },
      ];

      // Add terminal specific information
      if (terminal) {
        const terminalAddress = get(terminal, 'configuration.address');
        if (terminalAddress) {
          cells.push({
            content: terminalAddress,
            className: 'cr-truncate',
          });
        }
        const terminalSerialNumber = get(terminal, 'configuration.serialNumber');
        if (terminalSerialNumber) {
          cells.push({
            content: terminalSerialNumber,
            className: 'cr-truncate',
          });
        }
      }

      cells.push({
        content: 'Unknown',
        className: 'cr-truncate',
      });

      cells.push({
        content: (
          <Tooltip content="Unpair/Remove terminal" placement="top-end">
            <Button
              variant="danger"
              size="small"
              aria-label="Unpair terminal"
              onClick={() => {
                onDelete(terminal.ccTerminalID);
              }}
            >
              <IconDelete />
            </Button>
          </Tooltip>
        ),
      });

      return {
        key: terminal.ccTerminalID,
        cells,
      };
    });
  }

  return rows;
};

const GatewayTerminalListing = ({
  gatewayName,
  hasTerminalSupport,
  iOSGatewayOnly,
  hasSwiperSupportOnly,
  terminals,
  onEdit,
  onEditTerminal,
  onAddTerminal,
  onDeleteTerminal,
}) => {
  let emptyMessage = 'No Payment Terminals found!';

  if (hasTerminalSupport) {
    emptyMessage = hasRetailManagerContext()
      ? `No Payment Terminals found!  Add a Payment Terminal from the App Devices settings.`
      : 'No payment terminals found! Start processing payments by adding and configuring a terminal.';
  } else if (iOSGatewayOnly) {
    emptyMessage = hasRetailManagerContext() ? (
      'Manage payment terminals from the App Device settings.'
    ) : (
      <span>
        Manage and use payment terminals from the{' '}
        <TextLink href="https://itunes.apple.com/us/app/lightspeed-retail-point-sale/id848366907?ls=1&mt=8">
          Lightspeed Retail - Point of Sale iOS
        </TextLink>{' '}
        app.
      </span>
    );
  } else if (hasSwiperSupportOnly) {
    emptyMessage = 'Attach a compatible card reader to start processing payments.';
  }

  return (
    <Fragment>
      <div className="cr-flex cr-justify-between cr-items-center cr-mb-2">
        <div className="cr-flex cr-items-baseline">
          <Text heading3>
            Gateway: <var>{gatewayName}</var>
          </Text>
          <div className="cr-ml-2">
            <Text body bold>
              <TextLink
                onClick={() => {
                  onEdit();
                }}
                title="Edit"
              >
                Edit
              </TextLink>
            </Text>
          </div>
        </div>
        {hasTerminalSupport && (
          <Button
            variant="primary"
            fill
            onClick={() => {
              onAddTerminal();
            }}
          >
            Add Payment Terminal
          </Button>
        )}
      </div>
      {hasTerminalSupport ? (
        <Card>
          <CardSection noSpacing>
            <Table
              head={getTerminalHeaders(terminals)}
              body={getTerminalRows(terminals, onEditTerminal, onDeleteTerminal)}
              noDataMessage={emptyMessage}
              dataInit
            />
          </CardSection>
        </Card>
      ) : (
        <Card large>
          <CardSection>
            <div className="cr-flex cr-items-center cr-justify-center">
              <Text body dimmed>
                {emptyMessage}
              </Text>
            </div>
          </CardSection>
        </Card>
      )}
    </Fragment>
  );
};

GatewayTerminalListing.propTypes = {
  gatewayName: PropTypes.string,
  hasTerminalSupport: PropTypes.bool,
  iOSGatewayOnly: PropTypes.bool,
  hasSwiperSupportOnly: PropTypes.bool,
  terminals: PropTypes.arrayOf(PropTypes.shape()),
  onEdit: PropTypes.func,
  onEditTerminal: PropTypes.func,
  onAddTerminal: PropTypes.func,
  onDeleteTerminal: PropTypes.func,
};

GatewayTerminalListing.defaultProps = {
  gatewayName: 'Unknown',
  hasTerminalSupport: false,
  iOSGatewayOnly: false,
  hasSwiperSupportOnly: false,
  terminals: [],
  onEdit: () => {},
  onEditTerminal: () => {},
  onAddTerminal: () => {},
  onDeleteTerminal: () => {},
};

export default GatewayTerminalListing;
